import React, {
  createContext,
  MutableRefObject,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { connect } from "react-redux"
import {
  connect as connectTwilio,
  createLocalAudioTrack,
  createLocalVideoTrack,
  LocalAudioTrack,
  LocalAudioTrackPublication,
  LocalDataTrack,
  LocalParticipant,
  LocalTrack,
  LocalTrackPublication,
  LocalVideoTrack,
  LocalVideoTrackPublication,
  Participant,
  RemoteAudioTrack,
  RemoteDataTrack,
  RemoteTrack,
  RemoteVideoTrack,
  Room,
  StatsReport,
  TwilioError,
} from "twilio-video"

import { ActionWithoutPayload } from "types/redux"

import { DeviceManager } from "../Device"
import { TutorialState } from "../tutorialSteps"
import { getCall } from "../services/actions"
import {
  DEVICE_ID_DERMATOSCOPE,
  DEVICE_ID_OTOSCOPE,
  DEVICE_ID_STETHOSCOPE,
  DEVICE_ID_STETHOSCOPE_1,
  DEVICE_ID_STETHOSCOPE_2,
  DEVICE_ID_STETHOSCOPE_3,
  DEVICE_ID_STETHOSCOPE_4,
  RoomState,
  UNKNOWN_DEVICE,
} from "../types/types"
import { createUserLocalTracks } from "../utils"
import { getDeviceInfo } from "lib/video"
import styles from "./../Call.module.scss"
import config from "react-global-configuration"

export type ConnectParams = {
  tool_token?: string
  token: string
  roomName: string
}
export enum DEVICE_STATE {
  WAITING = "WAITING",
  USING = "USING",
  CLOSING = "CLOSING",
  UNPLUGGED = "UNPLUGGED",
}
const VIDEO_DEVICES = [DEVICE_ID_DERMATOSCOPE, DEVICE_ID_OTOSCOPE]

// Twilio errors after which rejoining the same room can succeed: signaling
// connection errors (53000-53002) and media/ICE connection failure (53405).
// Twilio raises them once its own signaling/media reconnection gives up.
const RECOVERABLE_DISCONNECT_ERROR_CODES = [53000, 53001, 53002, 53405]
// "Room completed": the room was ended on purpose (e.g. by the doctor),
// not a connection failure.
const ROOM_COMPLETED_ERROR_CODE = 53118
const MAX_REJOIN_ATTEMPTS = 3
const REJOIN_BASE_DELAY_MS = 2000

// consecutive bad polls (1/sec) of the kiosk's own camera/mic before telling the patient to refresh
const MEDIA_PROBLEM_DELAY_S = 15
const MEDIA_CHECK_INTERVAL_MS = 1000
// Media problems are only worth reporting while the connection itself is
// nominally up. During a Twilio reconnect or a scheduled rejoin the patient
// already sees the "reconnexion en cours" banner, and the two must not compete.
const CONNECTION_STABLE_STATES = [
  RoomState.WAITING_PEER,
  RoomState.LINKING_PEER,
]

const isDoctor = (participant: Participant) =>
  participant.identity.includes("doctor")

const doctorIsInRoom = (room: Room) =>
  Array.from(room.participants.values()).some(isDoctor)

export type VideoContextProps = {
  connect: (params: ConnectParams) => void
  disconnect: () => void
  connected: boolean
  state: RoomState
  mediaProblemDetected: boolean
  currentDevice?: { label: string; state: DEVICE_STATE }
  // DEVICE_IN_PROCESS: boolean
  children?: ReactNode
}

// Speech Recognition
const dataTrack = new LocalDataTrack()

const initialProps = {
  // DEVICE_IN_PROCESS: false,
  state: RoomState.ROOM_CONNECTION,
  connected: false,
}

const VideoContext = createContext<VideoContextProps>(
  initialProps as VideoContextProps,
)

export const useVideoContext = () => {
  const context = useContext(VideoContext)
  if (!context) {
    throw new Error("Context must be called inside Provider!")
  }
  return context
}

const VideoProvider = (
  props: React.PropsWithChildren<{ getCurrentCall: () => void }>,
) => {
  const [state, setState] = useState(RoomState.ROOM_CONNECTION)
  const [connected, setConnected] = useState(false)
  const tutorialStateRef = useRef<TutorialState>(null)
  const localMedia = useRef<HTMLDivElement>(null)
  const remoteMedia = useRef<HTMLDivElement>(null)
  const toolMedia = useRef<HTMLDivElement>(null)
  const [currentTool, setCurrentTool] = useState<{
    label: string
    state: DEVICE_STATE
  }>(undefined)
  const toolRef = useRef<{ label: string; state: DEVICE_STATE }>(null)

  const toolParticipantRef = useRef<LocalParticipant>(null)
  const patientParticipantRef = useRef<LocalParticipant>(null)

  const patientRoomRef = useRef<Room>(null)
  const toolRoomRef = useRef<Room>(null)
  const connectParamsRef = useRef<ConnectParams>(null)
  const manualDisconnectRef = useRef(false)
  // A pending connect or Twilio event can settle after the call screen is gone
  const unmountedRef = useRef(false)
  const rejoinAttemptsRef = useRef(0)
  const toolRejoinAttemptsRef = useRef(0)
  const rejoinTimerRef = useRef<ReturnType<typeof setTimeout>>(null)
  const toolRejoinTimerRef = useRef<ReturnType<typeof setTimeout>>(null)

  useEffect(() => {
    unmountedRef.current = false
    const handleBeforeUnload = () => {
      patientRoomRef.current?.disconnect()
      toolRoomRef.current?.disconnect()
    }
    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => {
      unmountedRef.current = true
      window.removeEventListener("beforeunload", handleBeforeUnload)
      clearTimeout(rejoinTimerRef.current)
      clearTimeout(toolRejoinTimerRef.current)
    }
  }, [])

  const [mediaProblemDetected, setMediaProblemDetected] = useState(false)
  const mediaCheckInterval = useRef<ReturnType<typeof setInterval> | null>(
    null,
  )
  const mediaProblemCounter = useRef(0)
  const checkingMedia = useRef(false)
  // an empty room is just the waiting room until the doctor has joined once
  const doctorJoined = useRef(false)
  // `state` drives the reconnection UI; mirror it so the media-check interval
  // reads the current value instead of the one captured when it was created
  const roomStateRef = useRef(state)
  const prevPatientVideoPackets = useRef<number | null>(null)

  const resetMediaProblemDetection = () => {
    mediaProblemCounter.current = 0
    prevPatientVideoPackets.current = null
    setMediaProblemDetected(false)
  }

  const clearMediaCheckInterval = () => {
    if (mediaCheckInterval.current) {
      clearInterval(mediaCheckInterval.current)
      mediaCheckInterval.current = null
    }
  }

  // a track whose packet count stops growing is no longer carrying media
  const packetsStalled = (
    packets: number | null,
    prevPackets: MutableRefObject<number | null>,
  ): boolean => {
    if (packets == null) return false
    const stalled =
      prevPackets.current !== null && packets <= prevPackets.current
    prevPackets.current = packets
    return stalled
  }

  // The patient's own camera and mic are published on the main room connection.
  // Connected tools publish through the separate tool connection, so a
  // dermatoscope or otoscope track can never be read as the patient camera,
  // nor hide a patient camera that stopped working.
  const patientPublications = (): {
    video?: LocalVideoTrackPublication
    audio?: LocalAudioTrackPublication
  } => {
    const participant = patientParticipantRef.current
    if (!participant) return {}
    return {
      video: Array.from(participant.videoTracks.values())[0],
      audio: Array.from(participant.audioTracks.values())[0],
    }
  }

  // The kiosk has no mute or camera-off control, so there is no voluntary case
  // to exclude here: any of these means the device really stopped delivering.
  const isLocalTrackBroken = (
    track: LocalAudioTrack | LocalVideoTrack,
  ): boolean =>
    track.isStopped ||
    track.mediaStreamTrack.readyState === "ended" ||
    track.mediaStreamTrack.muted

  const hasMediaProblem = async (_room: Room): Promise<boolean> => {
    const patient = patientPublications()
    if (
      !patient.video ||
      !patient.audio ||
      isLocalTrackBroken(patient.video.track) ||
      isLocalTrackBroken(patient.audio.track)
    ) {
      prevPatientVideoPackets.current = null
      return true
    }
    let reports: StatsReport[] = []
    try {
      reports = await _room.getStats()
    } catch {
      // a failed stats read tells us nothing, so do not report a problem
      return false
    }
    // Video only: Opus DTX legitimately stops sending audio packets during
    // silence, so a patient quietly listening would read as stalled.
    const stats = reports
      .flatMap((report) => report.localVideoTrackStats)
      .find((trackStats) => trackStats.trackSid === patient.video.trackSid)
    if (!stats) {
      prevPatientVideoPackets.current = null
      return false
    }
    return packetsStalled(stats.packetsSent, prevPatientVideoPackets)
  }

  const checkMediaStreams = async (_room: Room) => {
    if (checkingMedia.current) return
    if (
      !doctorJoined.current ||
      !CONNECTION_STABLE_STATES.includes(roomStateRef.current)
    ) {
      resetMediaProblemDetection()
      return
    }
    checkingMedia.current = true
    try {
      if (await hasMediaProblem(_room)) {
        mediaProblemCounter.current += 1
        if (mediaProblemCounter.current >= MEDIA_PROBLEM_DELAY_S) {
          setMediaProblemDetected(true)
        }
      } else {
        mediaProblemCounter.current = 0
        setMediaProblemDetected(false)
      }
    } finally {
      checkingMedia.current = false
    }
  }

  useEffect(() => {
    roomStateRef.current = state
  }, [state])

  useEffect(
    () => () => {
      if (mediaCheckInterval.current) clearInterval(mediaCheckInterval.current)
    },
    [],
  )

  const connectTools = async ({
    roomName,
    tool_token,
  }: {
    roomName: string
    tool_token: string
  }) => {
    console.log("connecting tool to the room...", roomName)
    return connectTwilio(tool_token, {
      name: roomName,
      tracks: [],
    })
      .then((_room) => {
        if (unmountedRef.current) {
          _room.disconnect()
          return
        }
        toolRoomRef.current = _room
        toolParticipantRef.current = _room.localParticipant
        toolRejoinAttemptsRef.current = 0
        _room.on("reconnecting", (error: TwilioError) => {
          console.warn("Tool room reconnecting", error?.code, error?.message)
        })
        _room.on("reconnected", () => {
          console.warn("Tool room reconnected")
        })
        _room.once("disconnected", (__room: Room, error?: TwilioError) => {
          toolRoomRef.current = null
          toolParticipantRef.current = null
          if (!error || manualDisconnectRef.current || unmountedRef.current)
            return
          console.error(
            `Tool room disconnected: ${error.code} ${error.message}`,
            { route: "videoContext::connectTools::disconnected" },
          )
          if (
            RECOVERABLE_DISCONNECT_ERROR_CODES.includes(error.code) &&
            toolRejoinAttemptsRef.current < MAX_REJOIN_ATTEMPTS
          ) {
            toolRejoinAttemptsRef.current += 1
            clearTimeout(toolRejoinTimerRef.current)
            toolRejoinTimerRef.current = setTimeout(
              () => connectTools({ roomName, tool_token }),
              REJOIN_BASE_DELAY_MS * toolRejoinAttemptsRef.current,
            )
          }
        })
      })
      .catch((e) =>
        console.error(e, {
          route: "videoContext::connectTools::connectTwilio",
        }),
      )
  }

  const scheduleRejoin = (): boolean => {
    if (
      unmountedRef.current ||
      manualDisconnectRef.current ||
      !connectParamsRef.current
    )
      return false
    if (rejoinAttemptsRef.current >= MAX_REJOIN_ATTEMPTS) return false
    rejoinAttemptsRef.current += 1
    console.warn(
      `Room rejoin attempt ${rejoinAttemptsRef.current}/${MAX_REJOIN_ATTEMPTS}`,
    )
    setState(RoomState.RECONNECTION)
    clearTimeout(rejoinTimerRef.current)
    rejoinTimerRef.current = setTimeout(
      () => connect(connectParamsRef.current, true),
      REJOIN_BASE_DELAY_MS * rejoinAttemptsRef.current,
    )
    return true
  }

  const connect = async (
    { token, tool_token, roomName }: ConnectParams,
    isRejoin = false,
  ) => {
    console.log("connecting to the room...", roomName)
    connectParamsRef.current = { token, tool_token, roomName }
    manualDisconnectRef.current = false
    if (!isRejoin) rejoinAttemptsRef.current = 0
    clearMediaCheckInterval()
    // a rejoin continues the same consultation, so keep knowing the doctor came
    if (!isRejoin) doctorJoined.current = false
    resetMediaProblemDetection()
    setState(RoomState.ROOM_CONNECTING)

    if (tool_token && !toolRoomRef.current)
      await connectTools({ tool_token, roomName })

    return await createUserLocalTracks()
      .then((localTracks: Array<LocalTrack>) => {
        const videoTrack = localTracks.find((x) => x.kind === "video")
        videoTrack && trackSubscribed(videoTrack as LocalVideoTrack, localMedia)
        localTracks.push(dataTrack)
        return connectTwilio(token, {
          name: roomName,
          tracks: localTracks,
          networkQuality: { local: 1, remote: 1 },
        })
      })
      .then((room) => {
        if (unmountedRef.current) {
          stopLocalPublications(room)
          room.disconnect()
          return
        }
        patientRoomRef.current = room
        patientParticipantRef.current = room.localParticipant
        rejoinAttemptsRef.current = 0
        setConnected(true)
        roomEvent(room)
      })
      .catch((e) => {
        console.error(e, {
          route: "videoContext::connect::createUserLocalTracks",
        })
        if (unmountedRef.current) return
        if (!scheduleRejoin()) {
          setConnected(false)
          setState(RoomState.CONNECTION_LOST)
          props.getCurrentCall()
        }
      })
  }

  const disconnect = () => {
    manualDisconnectRef.current = true
    clearTimeout(rejoinTimerRef.current)
    clearTimeout(toolRejoinTimerRef.current)
    setConnected(false)
    clearMediaCheckInterval()
    doctorJoined.current = false
    resetMediaProblemDetection()
    patientRoomRef.current?.disconnect()
    patientRoomRef.current = null
    toolRoomRef.current?.disconnect()
    toolRoomRef.current = null
  }

  const stopLocalPublications = (_room: Room) => {
    _room.localParticipant.tracks.forEach(
      (publication: LocalTrackPublication) => {
        if (
          publication.track.kind === "audio" ||
          publication.track.kind === "video"
        ) {
          publication.track.stop()
          const attachedElements = publication.track.detach()
          attachedElements.forEach((element) => element.remove())
        }
      },
    )
  }

  const roomEvent = (_room: Room) => {
    setState(
      doctorIsInRoom(_room) ? RoomState.LINKING_PEER : RoomState.WAITING_PEER,
    )

    _room.participants.forEach(participantEvent)

    clearMediaCheckInterval()
    mediaCheckInterval.current = setInterval(
      () => void checkMediaStreams(_room),
      MEDIA_CHECK_INTERVAL_MS,
    )

    _room.on("participantConnected", participantEvent)

    _room.on("participantDisconnected", (participant) => {
      console.warn(`Participant "${participant.identity}" disconnected`)
      if (isDoctor(participant)) setState(RoomState.WAITING_PEER)
    })

    // Twilio raises "reconnecting" when the media connection degrades
    // (RTCIceConnectionState "disconnected"/"failed") or the signaling
    // websocket drops, and "reconnected" once its own recovery succeeds.
    _room.on("reconnecting", (error: TwilioError) => {
      console.error(
        `Room reconnecting: ${error?.code} ${error?.message}`,
        { route: "videoContext::roomEvent::reconnecting" },
      )
      setState(RoomState.RECONNECTION)
    })

    _room.on("reconnected", () => {
      console.warn("Room reconnected")
      setState(
        doctorIsInRoom(_room) ? RoomState.LINKING_PEER : RoomState.WAITING_PEER,
      )
    })

    _room.localParticipant.on(
      "trackPublicationFailed",
      (error: TwilioError, track: LocalTrack) => {
        console.error(
          `Track publication failed (${track.kind}): ${error?.code} ${error?.message}`,
          { route: "videoContext::roomEvent::trackPublicationFailed" },
        )
      },
    )

    _room.localParticipant.on(
      "networkQualityLevelChanged",
      (level: number) => {
        if (level <= 1) console.error(`Low network quality level: ${level}`, { route: 'videoContext::networkQualityLevelChanged' })
      },
    )

    _room.once("disconnected", (room, error?: TwilioError) => {
      console.warn(`[Bye bye] Room disconnected`)
      stopLocalPublications(room)
      clearMediaCheckInterval()
      resetMediaProblemDetection()
      patientRoomRef.current = null
      setConnected(false)
      if (error) {
        console.error(
          `You were disconnected from the Room: ${error.code} ${error.message}`,
          { route: "videoContext::roomEvent::disconnected" },
        )
        // Rejoin the room when the disconnection comes from a recoverable
        // network failure; the doctor side keeps the room alive.
        if (
          RECOVERABLE_DISCONNECT_ERROR_CODES.includes(error.code) &&
          scheduleRejoin()
        )
          return
        if (error.code !== ROOM_COMPLETED_ERROR_CODE)
          setState(RoomState.CONNECTION_LOST)
      }
      if (!unmountedRef.current) props.getCurrentCall()
    })
  }

  const sendTutorialState = (state: TutorialState | null) => {
    tutorialStateRef.current = state
    resendTutorialState()
  }

  const resendTutorialState = () => {
    if (tutorialStateRef.current)
      dataTrack.send(JSON.stringify(tutorialStateRef.current))
  }

  const participantEvent = (participant: Participant) => {
    console.warn(`Participant "${participant.identity}" connected`)

    if (isDoctor(participant)) {
      doctorJoined.current = true
      setState(RoomState.LINKING_PEER)

      // A doctor joining mid-tutorial has missed every step sent so far. Twilio drops what is sent
      // before the doctor subscribes to our data track, hence the resend on "request-tutorial-state".
      resendTutorialState()

      // Remote peer media reconnection (doctor side network instability)
      participant.on("reconnecting", () => {
        console.warn(`Participant "${participant.identity}" reconnecting`)
        setState(RoomState.PEER_CONNECTION_LOST)
      })

      participant.on("reconnected", () => {
        console.warn(`Participant "${participant.identity}" reconnected`)
        setState(RoomState.LINKING_PEER)
      })
    }

    participant.on(
      "trackSubscribed",
      (track: RemoteVideoTrack | RemoteAudioTrack | RemoteDataTrack) => {
        if (!isDoctor(participant)) return
        trackSubscribed(track, remoteMedia)
        if (track.kind === "data") resendTutorialState()
      },
    )

    participant.on("trackUnsubscribed", (e) => trackUnsubscribed(e))
  }

  const getDeviceId = (devices: MediaDeviceInfo[], label: string) => {
    return devices.find((device: MediaDeviceInfo) =>
      device.label.includes(label),
    )
  }

  const onDeviceChange = async (): Promise<MediaDeviceInfo[]> => {
    return await new Promise((resolve) => {
      navigator.mediaDevices.addEventListener(
        "devicechange",
        () => resolve(getDeviceInfo()),
        { once: true },
      )
    })
  }

  const setDevice = (currentDevice?: {
    label: string
    state: DEVICE_STATE
  }) => {
    toolRef.current = currentDevice
    setCurrentTool(toolRef.current)
  }

  const awaitingDevice = async (
    label: string,
  ): Promise<MediaDeviceInfo | undefined> => {
    const device = await getDeviceInfo().then((devices) => {
      return getDeviceId(devices, label)
    })
    if (device) {
      setDevice({ label, state: DEVICE_STATE.USING })
      return device
    } else {
      // the waiting tool has not yet appear in the mediaDevice list.
      setDevice({ label, state: DEVICE_STATE.WAITING })
      return await onDeviceChange().then(() => awaitingDevice(label))
    }
  }

  const checkDevices = async (label: string): Promise<boolean> => {
    const device = await getDeviceInfo().then((devices) => {
      return getDeviceId(devices, label)
    })
    return !!device
  }

  const trackSubscribed = (
    track: RemoteTrack | LocalTrack,
    div: RefObject<HTMLDivElement>,
  ) => {
    console.log("trackSubscribed", track)
    track.kind !== "data" && div.current?.appendChild(track.attach())
    track.kind === "data" && track.on("message", onMessageReceived)
  }
  const onMessageReceived = async (message: string) => {
    console.log("On Message received")
    if (message === "stopDevice") {
      return stopTool()
    }
    if (message === "request-tutorial-state") {
      return resendTutorialState()
    }
    if (message === "activate-dermatoscope") {
      joinTool(await awaitingDevice(DEVICE_ID_DERMATOSCOPE))
    } else if (message === "activate-otoscope") {
      joinTool(await awaitingDevice(DEVICE_ID_OTOSCOPE))
    } else if (message === "activate-stethoscope") {
      if (await checkDevices(DEVICE_ID_STETHOSCOPE)) {
        joinTool(await awaitingDevice(DEVICE_ID_STETHOSCOPE))
      } else if (await checkDevices(DEVICE_ID_STETHOSCOPE_1)) {
        joinTool(await awaitingDevice(DEVICE_ID_STETHOSCOPE_1))
      } else if (await checkDevices(DEVICE_ID_STETHOSCOPE_2)) {
        joinTool(await awaitingDevice(DEVICE_ID_STETHOSCOPE_2))
      } else if (await checkDevices(DEVICE_ID_STETHOSCOPE_3)) {
        joinTool(await awaitingDevice(DEVICE_ID_STETHOSCOPE_3))
      } else if (await checkDevices(DEVICE_ID_STETHOSCOPE_4)) {
        joinTool(await awaitingDevice(DEVICE_ID_STETHOSCOPE_4))
      } else {
        setDevice({
          label: DEVICE_ID_STETHOSCOPE,
          state: DEVICE_STATE.UNPLUGGED,
        })
        console.error("no STETHOSCOPE DETECTED", {
          route: "videoContext::onMessageReceived",
        })
      }
    }
  }

  const stopTool = ({ withoutAdvice }: { withoutAdvice?: boolean } = {}) => {
    // Nous sommes sorti du contexte, à cause de l'eventListener sur message
    // Nous utilisons donc une référence à l'objet currentTool
    const toolUsed = toolRef.current

    if (withoutAdvice) setDevice(undefined)
    else
      setDevice({
        label: toolUsed?.label || "",
        state: DEVICE_STATE.CLOSING,
      })

    console.log("[trying to stop : ]", toolParticipantRef?.current)
    console.log("[Automatic recognize shutdown tool]", withoutAdvice)
    if (toolParticipantRef.current) {
      const publications =
        Array.from(toolParticipantRef.current.tracks?.values()) ?? []
      publications.forEach((publication: LocalTrackPublication) => {
        toolParticipantRef.current?.unpublishTrack(publication.track)
        trackUnsubscribed(publication.track)
      })
    }
  }

  const preventDefaultAudioStreamTrack = () => {
    // Invalider l'audio principal du patient précédement publier pour le remplacer par un nouveau
    // afin de prévenir des pertes de son de certaines pharmacies après l'utilisation d'outils connectés
    console.log("VideoContext::preventDefaultAudioStreamTrack")
    if (patientParticipantRef) {
      createUserLocalTracks()
        .then((localTracks) => {
          const audioTrack = localTracks.find((x) => x.kind === "audio")
          patientParticipantRef.current.audioTracks.forEach((publication) => {
            patientParticipantRef.current.unpublishTrack(publication.track)
          })
          patientParticipantRef.current.publishTrack(audioTrack)
        })
        .catch((e) => {
          console.error(e, {
            route:
              "videoContext::preventDefaultStreamTracks::createUserLocalTracks",
          })
        })
    } else {
      console.warn(
        "videoContext::preventDefaultStreamTracks::patientParticipantRef",
        "La référence du participant n'existe pas dans le contexte. Impossible de republier l'audio.",
      )
    }
  }

  const joinTool = async (device?: MediaDeviceInfo) => {
    if (!device) return
    const currentDeviceId = device.deviceId
    const createTrack =
      device.kind === "audioinput"
        ? createLocalAudioTrack
        : createLocalVideoTrack

    await createTrack({
      deviceId: { exact: currentDeviceId },
    })
      .then((track: LocalVideoTrack | LocalAudioTrack) => {
        toolParticipantRef.current?.publishTrack(track)
        trackSubscribed(track as LocalVideoTrack | LocalAudioTrack, toolMedia)
        preventDefaultAudioStreamTrack()
      })
      .catch((e) => {
        console.error(e, {
          route: "videoContext::joinTool::createTrack",
        })
        const label = currentTool?.label ?? UNKNOWN_DEVICE
        setDevice({
          label,
          state: DEVICE_STATE.UNPLUGGED,
        })
      })

    navigator.mediaDevices.addEventListener(
      "devicechange",
      async () => {
        const devices = await getDeviceInfo()
        // If the deviceChange event return an array where the currentDevice is missing
        // We automatically stop the current tool
        // exemple : if the patient shutdown the device or unplug it
        if (!devices.find((device) => device.deviceId === currentDeviceId))
          stopTool({ withoutAdvice: true })
      },
      { once: true },
    )
  }

  const trackUnsubscribed = (track: RemoteTrack | LocalTrack) => {
    track.kind !== "data" &&
      track.detach().forEach((element: HTMLMediaElement) => element.remove())
  }

  const isVideoDevice = (): boolean =>
    !!currentTool &&
    VIDEO_DEVICES.includes(currentTool.label) &&
    currentTool.state === DEVICE_STATE.USING

  return (
    <VideoContext.Provider
      value={{
        connect,
        disconnect,
        state,
        connected,
        mediaProblemDetected,
      }}
    >
      <div id={styles.CallContainer}>
        <DeviceManager
          device={currentTool}
          onStop={() => {
            setDevice(undefined)
          }}
          onTutorialState={sendTutorialState}
        />
        <div className={styles.RemoteContainer}>
          <div
            id={styles.RemoteMedia}
            className={`${styles.LoadingBackgroundVideo} ${isVideoDevice() ? styles.VideoDeviceIsActive : null
              }`}
            ref={remoteMedia}
          />
          <div className={styles.ChildrenContainer}>{props.children}</div>
          {isVideoDevice() && (
            <div
              id={styles.ToolMedia}
              className={styles.LoadingBackgroundVideo}
              ref={toolMedia}
            />
          )}
        </div>
        <div>
          <div
            id={styles.LocalMedia}
            className={styles.LoadingBackgroundVideo}
            ref={localMedia}
          />
          <div className={styles.HelpSupport}>
            {" "}
            Support : {config.get("support")}{" "}
          </div>
        </div>
      </div>
    </VideoContext.Provider>
  )
}

export { VideoContext }

const mapDispatchToProps = (dispatch: (a: ActionWithoutPayload) => void) => {
  return {
    getCurrentCall: () => dispatch(getCall()),
  }
}
export default connect(null, mapDispatchToProps)(VideoProvider as any) as any
